import { filter } from 'rxjs/operators';
import { Component, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { PostalAddress } from '../../../common/model/postal-address';
import { FormComponentBase } from '../../../core/component/forms/form-component.base';
import { FormControlEx } from '../../../core/component/forms/form-control';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { CommandHandlerResolver } from '../../../core/service/command/command-handler-resolver.service';
import { ComponentMessageDispatcher } from '../../../core/service/component-message-dispather.service';
import { MessageService } from '../../../core/service/message.service';
import { NativeWindowService } from '../../../core/service/native-window.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { SmsStatus } from '../../model/const/enums';
import { Sms } from '../../model/sms';
import { SmsService } from '../../service/data/sms.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { Observable } from 'rxjs';

@Component({
    selector: 'app-send-lease-agreement-sms',
    templateUrl: './send-tenant-sms-form.component.html',
    styleUrls: ['./send-tenant-sms-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SendTenantSMSFormComponent extends FormComponentBase<Sms> {
  constructor(
    private _messageDispather: ComponentMessageDispatcher,
    private _nativeWindow: NativeWindowService,
    activatedRoute: ActivatedRoute,
    dispatcher: CommandDispatcher,
    protected fb: FormBuilder,
    private _fc: FormControlExFactory,
    handlerResolver: CommandHandlerResolver,
    protected service: SmsService,
    messageService: MessageService,
    navigation: NavigationService
  ) {
    super(activatedRoute, service, messageService, navigation, dispatcher, handlerResolver);

    this.AddSubscription(
      this._messageDispather.messages.pipe(filter(message => message.tenants)).subscribe(message => {
        _messageDispather.messageDelivered();
        this.recipients = message.tenants.slice();
        this.showRecipients = this.recipients.length > 0;
      })
    );
    this.restoreRecipients();
  }

  recipients: any[];
  showRecipients: boolean = false;
  showPreview = false;

  senderAddress: PostalAddress;
  get canSend(): boolean {
    return this.form && this.form.valid;
  }

  get hasRecipients(): boolean {
    return Array.isArray(this.recipients) && this.recipients.length > 0;
  }

  get isSendDisabled(): boolean {
    return !this.form || this.form.invalid || this.form.disabled || !this.hasRecipients;
  }

  get showSend(): boolean {
    return this.dataContext && this.dataContext.status === SmsStatus.Created;
  }

  protected createModel(): Sms {
    return new Sms();
  }

  protected getFormGroup(): FormGroup {
    return this.fb.group({
      message: this._fc.createWithOptions('MAIL_FORM.BODY_SMS_LABEL', this.dataContext.message, { required: true, length: 918 }),
    });
  }

  protected init(smsMessage: Sms) {
    super.init(smsMessage);
  }

  private _getFormData() {
    let data = this.form.value;
    data.leaseAgreements = (this.recipients ?? []).map(recipient => recipient.id);
    return data;
  }

  private clearRecipients() {
    this.recipients = [];
    this.showRecipients = false;
  }

  private restoreRecipients() {
    if (!this.hasRecipients) this.handleMissingRecipients();
  }

  private handleMissingRecipients() {
    this.clearRecipients();
    this.messageService.showError('COMMON.RECIPIENTS_REQUIRED');
    this.navigation.navigate(['sms', 'recipients']);
  }

  cancel() {
    this.clearRecipients();
    this.navigation.navigateBack();
  }

  canDeactivate(): boolean | Observable<boolean> | Promise<boolean> {
    //
    // Avoid save changes message
    //
    return true;
  }

  send() {
    if (this.form.invalid || this.form.pristine || this.form.disabled || !this.hasRecipients) return;
    this.form.disable();
    this.AddSubscription(
      this.service.sendSMSToTenants(this._getFormData()).subscribe(
        () => {
          this.clearRecipients();
          this.formSubmitted();
          this.messageService.showSuccess('SUCCESS.SMS_SENT');
          this.navigation.navigateBack();
        },
        error => {
          this.messageService.showError(error);
          this.form.enable();
        }
      )
    );
  }
}
